import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    PriceChange as PriceChangeIcon,
} from "@mui/icons-material";
import { useAuth, useLoading, useSnackbar, useNavigation } from "../../Context/Contexts";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import LoadingSection from "../../components/LoadingSection";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import ResponsivePagination from "../../components/ResponsivePagination";
import { Option, VariantOption } from "../../Types/CommonTypes";
import { SalesPrice, SalesPriceListResponse, SalesPricePayload } from "../../Types/Sales/SalesPriceTypes";

import { ItemAutoComplete, VariantAutoComplete, CurrencyAutoComplete } from "../../components/AutoComplete/AutoCompletes";

import ItemListActions from "../../components/Item/ItemListActions";

interface SalesPriceForm {
    id?: number | null;
    itemNo?: string | null;
    itemDescription?: string | null;
    variantId?: number | null;
    variantDescription?: string | null;
    variantBarcode?: string | null;
    unitPrice?: number | null;
    originalPrice?: number | null;
    startDate?: string | null;
    endDate?: string | null;
    pricesIncludingVat?: boolean;
    currencyCode?: string | null;
    priceGroupCode?: string | null;
    isCreateNew?: boolean;
}

const defaultQuery = { page: 0, pageSize: 12, filter: "", filters: [] as { property: string; value: string }[] };

const PricelistListPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { itemNo = "" } = params || {};
  const { navigate } = useNavigation();
    const { t } = useTranslation();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const queryInit = { ...defaultQuery, filters: [...defaultQuery.filters] };

    let skipCache = false;

    if (itemNo) {
        queryInit.filters.push({ property: "ext_ItemNoRef", value: itemNo });
        skipCache = true;
    }

    const [query, setQuery] = useCachedListQuery(queryInit, "sales_price_list", skipCache);
    const [prices, setPrices] = useState<SalesPrice[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [linesLoading, setLinesLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [editingPrice, setEditingPrice] = useState<SalesPriceForm | null>(null);

    const itemHelperText = useMemo(() => {
        if (!editingPrice?.isCreateNew) return "";
        if (editingPrice?.itemNo) return "";
        return t("help.itemRequired");
    }, [editingPrice, t]);

    const unitPriceError = useMemo(() => !editingPrice?.unitPrice && editingPrice?.unitPrice !== 0, [editingPrice]);

    const formatDateForInput = (value?: string | null) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        return date.toISOString().slice(0, 10);
    };

    const formatDisplayDate = (value?: string | null) => {
        if (!value) return t("common.notSet");
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return t("common.notSet");
        return date.toLocaleDateString();
    };

    const formatCurrency = (amount: number, currency?: string | null) => {
        if (Number.isNaN(amount)) return "";
        return new Intl.NumberFormat(undefined, {
            style: currency ? "currency" : "decimal",
            currency: currency ?? undefined,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const fetchPrices = useCallback(async () => {
        try {
            setLinesLoading(true);
            showLoading();
            const response = await authService.post<SalesPriceListResponse>("/salesPriceApi/GetList", query);
            setPrices(response?.items ?? []);
            setTotalCount(response?.count ?? 0);
        } catch (error) {
            console.error("Failed to load price list", error);
            showSnackbar(t("common.fetchDataError"), "error");
        } finally {
            hideLoading();
            setLinesLoading(false);
        }
    }, [authService, hideLoading, query, showLoading, showSnackbar, t]);

    useEffect(() => {
        fetchPrices();
    }, [fetchPrices]);

    const handleSearchSubmit = (event?: React.FormEvent) => {
        event?.preventDefault();
        let property = filterProperty;
        if (!property?.trim() && filter?.trim()) {
            property = "Default";
        }

        if (property?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(property, filter);
            const exists = query.filters?.some((f) => f.property === newFilter.property && f.value === newFilter.value);
            if (exists) {
                setFilter("");
                setFilterProperty("");
                return;
            }
            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, page: 0, filter: "", filters: newFilters });
        } else {
            setQuery({ ...query, page: 0, filter });
        }
        setFilter("");
        setFilterProperty("");
    };

    const handleRemoveFilter = (index: number) => {
        const updatedFilters = query.filters?.filter((_, i) => i !== index) ?? [];
        setQuery({ ...query, page: 0, filters: updatedFilters });
    };

    const handleAddPrice = () => {
        setEditingPrice({
            isCreateNew: true,
            unitPrice: null,
            originalPrice: null,
            startDate: null,
            endDate: null,
            itemNo: itemNo?.length > 0 ? itemNo : null
        });
    };

    const handleEditPrice = (price: SalesPrice) => {
        setEditingPrice({
            id: price.id,
            itemNo: price.itemNo,
            itemDescription: price.itemDescription,
            variantId: price.variantId,
            variantDescription: price.variantDescription,
            variantBarcode: price.variantBarcode,
            unitPrice: price.unitPrice,
            originalPrice: price.originalPrice ?? null,
            startDate: price.startDate ?? null,
            endDate: price.endDate ?? null,
            pricesIncludingVat: price.pricesIncludingVat,
            currencyCode: price.currencyCode,
            priceGroupCode: price.priceGroupCode,
            isCreateNew: false,
        });
    };

    const handleDeletePrice = async (price: SalesPrice) => {
        try {
            showLoading();
            await authService.post("/salesPriceApi/Delete", { id: price.id });
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            fetchPrices();
        } catch (error: any) {
            console.error("Failed to delete price", error);
            showSnackbar(`${t("dialog.deleteFailed")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    };

    const handleVariantChange = (value: VariantOption | null) => {
        setEditingPrice((prev) =>
            prev
                ? {
                    ...prev,
                    variantId: value?.id ?? null,
                    variantDescription: value
                        ? [value.colourDescription, value.sizeDescription, value.lengthDescription]
                            .filter((x) => x && x.trim().length > 0)
                            .join(", ")
                        : null,
                    variantBarcode: value?.barcode ?? null,
                }
                : prev
        );
    };

    const handleItemChange = (option: Option | null) => {
        setEditingPrice((prev) =>
            prev
                ? {
                    ...prev,
                    itemNo: option?.value ?? null,
                    itemDescription: option?.description ?? null,
                    variantId: null,
                    variantDescription: null,
                    variantBarcode: null,
                }
                : prev
        );
    };

    const handleFieldChange = <K extends keyof SalesPriceForm>(field: K, value: SalesPriceForm[K]) => {
        setEditingPrice((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleSavePrice = async () => {
        if (!editingPrice) return;
        if (editingPrice.isCreateNew && !editingPrice.itemNo) {
            showSnackbar(t("dialog.itemRequired"), "warning");
            return;
        }
        if (editingPrice.unitPrice == null || Number.isNaN(editingPrice.unitPrice)) {
            showSnackbar(t("dialog.unitPriceRequired"), "warning");
            return;
        }

        const payload: SalesPricePayload = {
            id: editingPrice.id ?? undefined,
            itemNo: editingPrice.itemNo ?? undefined,
            variantId: editingPrice.variantId ?? undefined,
            unitPrice: editingPrice.unitPrice ?? undefined,
            currencyCode: editingPrice.currencyCode ?? undefined,
            originalPrice: editingPrice.originalPrice ?? undefined,
            listPrice: editingPrice.unitPrice ?? undefined,
            startDate: editingPrice.startDate ?? undefined,
            endDate: editingPrice.endDate ?? undefined,
        };

        try {
            showLoading();
            const response = await authService.post<SalesPrice>("/salesPriceApi/CreateOrUpdate", payload);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
            setEditingPrice(null);
            if (response) {
                setPrices((prev) => {
                    const index = prev.findIndex((p) => p.id === response.id);
                    if (index === -1) {
                        return [response, ...prev];
                    }
                    const clone = [...prev];
                    clone[index] = response;
                    return clone;
                });
                fetchPrices();
            } else {
                fetchPrices();
            }
        } catch (error: any) {
            console.error("Failed to save price", error);
            showSnackbar(`${t("dialog.failedToSaveChanges")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
        }
    };


  const handleNavigateImport = () => {
    navigate({ path: "/sales/pricelist/import", params: { importId: crypto.randomUUID() } });
  };


  const handleExport = async () => {
    if (!query.filter && (!query.filters || query.filters.length == 0)) {
      showSnackbar(t("dialog.missingFilters"), "warning");
    }

    try {
      const response = await authService.post<Blob>(
        "/salesPriceApi/GetPriceListCsv",
        query,
        "blob"
      );

      const csvBlob = response instanceof Blob
        ? response
        : new Blob([response as any], { type: "text/csv;charset=utf-8" });

      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PriceList.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error : any) {
      console.error("Failed to download Excel", error);
      showSnackbar(`${t("common.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
    }
  };


    return (
      <CenteredListCard title={t("common.pricelist")}>
        <Box sx={{ marginBottom: 2 }}>
          <ItemListActions
            onImport={() => handleNavigateImport()}
            onExport={() => handleExport()}
          />
        </Box>
            <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_ItemNoRef">{t("common.item")}</option>
                            <option value="ext_CurrencyCode">{t("common.currencyCode")}</option>
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>
                        {(() => {
                            switch (filterProperty) {
                                case "ext_ItemNoRef":
                                    return (
                                        <ItemAutoComplete
                                            onChange={(o) => setFilter(o?.value || "")}
                                        />
                                    );
                                case "ext_CurrencyCode":
                                    return (
                                        <CurrencyAutoComplete
                                            onChange={(o) => setFilter(o?.value || "")}
                                        />
                                    );

                                default:
                                    return (
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(event) => setFilter(event.target.value)}
                                            value={filter}
                                        />
                                    );
                            }
                        })()}
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button variant="contained" type="submit">
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {query.filters?.map((filterItem, index) => (
                    <Chip
                        key={`${filterItem.property}-${filterItem.value}-${index}`}
                        label={`${filterItem.property}: ${filterItem.value}`}
                        onDelete={() => handleRemoveFilter(index)}
                    />
                ))}
            </Box>

            <Button variant="contained" onClick={handleAddPrice} startIcon={<AddIcon />} color="success" sx={{ mb: 2 }}>
                {t("common.create")}
            </Button>

            <LoadingSection loading={linesLoading}>
                {prices.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("common.noData")}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {prices.map((price) => (
                            <Grid item xs={12} sm={6} md={4} key={price.id}>
                                <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 2 }}>
                                    <Grid container>
                                        <Grid item xs={10}>
                                            <ListCardHeader
                                                icon={<PriceChangeIcon fontSize="small" color="action" />}
                                                title={price.itemDescription ?? price.itemNo ?? ""}
                                                hasEdit
                                            />
                                            <Typography variant="body2">
                                                {t("common.item")}: {price.itemNo}
                                            </Typography>
                                            {price.variantDescription && (
                                                <Typography variant="body2">
                                                    {t("common.variant")}: {price.variantDescription}
                                                </Typography>
                                            )}
                                            {price.variantBarcode && (
                                                <Typography variant="body2">
                                                    {t("common.barcode")}: {price.variantBarcode}
                                                </Typography>
                                            )}
                                            <Typography variant="body2">
                                                {t("common.unitPrice")}: {formatCurrency(price.unitPrice, price.currencyCode)}
                                            </Typography>
                                            {price.originalPrice != null && (
                                                <Typography variant="body2">
                                                    {t("common.originalPrice")}: {formatCurrency(price.originalPrice, price.currencyCode)}
                                                </Typography>
                                            )}
                                            {/*{price.priceGroupCode && (*/}
                                            {/*    <Typography variant="body2">*/}
                                            {/*        {t("common.priceGroup")}: {price.priceGroupCode}*/}
                                            {/*    </Typography>*/}
                                            {/*)}*/}

                                            <Typography variant="body2">
                                                {t("common.currency")}: {price.currencyCode}
                                            </Typography>

                                            <Typography variant="body2">
                                                {t("common.startDate")}: {formatDisplayDate(price.startDate)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {t("common.endDate")}: {formatDisplayDate(price.endDate)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {t("common.includeVat")}: {price.pricesIncludingVat ? t("common.yes") : t("common.no")}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <Stack spacing={1} direction="row" justifyContent="flex-end">
                                                <Tooltip title={t("common.edit")}>
                                                    <IconButton size="small" color="primary" onClick={() => handleEditPrice(price)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t("common.delete")}>
                                                    <IconButton size="small" color="error" onClick={() => handleDeletePrice(price)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </LoadingSection>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(page) => setQuery({ ...query, page })}
                sx={{ mt: 2 }}
            />

            <Dialog
                open={!!editingPrice}
                onClose={() => setEditingPrice(null)}
                fullWidth
                maxWidth="sm"
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editingPrice?.isCreateNew ? t("common.create") : t("common.edit")}
                    </Typography>
                    <Stack spacing={2}>
                        <ItemAutoComplete
       
                            onChange={handleItemChange}
                            valueId={editingPrice?.itemNo ?? null}
                            disabled={!editingPrice?.isCreateNew}
                            error={editingPrice?.isCreateNew && !editingPrice?.itemNo}
                            helperText={itemHelperText}
                        />
                        {/*<VariantAutoComplete*/}
                        {/*    itemNo={editingPrice?.itemNo ?? null}*/}
                        {/*    valueId={editingPrice?.variantId ?? null}*/}
                        {/*    onChange={handleVariantChange}*/}
                        {/*    disabled={!editingPrice?.itemNo}*/}
                        {/*disabled={!editingPrice?.isCreateNew}*/}
                        {/*/>*/}
                        <TextField
                            label={t("common.unitPrice")}
                            type="number"
                            value={editingPrice?.unitPrice ?? ""}
                            onChange={(e) => handleFieldChange("unitPrice", e.target.value === "" ? null : Number(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                            error={unitPriceError}
                        />
                        <CurrencyAutoComplete
                            valueId={editingPrice?.currencyCode ?? null}
                            onChange={(selected) => handleFieldChange("currencyCode", selected?.value || "")}
                            disabled={!editingPrice?.itemNo}
                            selectFirst={ true}
                        />
                        <TextField
                            label={t("common.originalPrice")}
                            type="number"
                            value={editingPrice?.originalPrice ?? ""}
                            onChange={(e) => handleFieldChange("originalPrice", e.target.value === "" ? null : Number(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label={t("common.startDate")}
                            type="date"
                            value={formatDateForInput(editingPrice?.startDate)}
                            onChange={(e) => handleFieldChange("startDate", e.target.value || null)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label={t("common.endDate")}
                            type="date"
                            value={formatDateForInput(editingPrice?.endDate)}
                            onChange={(e) => handleFieldChange("endDate", e.target.value || null)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => setEditingPrice(null)}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSavePrice}>
                            {t("common.save")}
                        </Button>
                    </Stack>
                </Box>
            </Dialog>
        </CenteredListCard>
    );
};

export default PricelistListPage;
